import { useEffect } from 'react';

import { observer } from 'mobx-react';

import { useStores } from '../../../../stores/useStores';
import type { JackpotWidgetDataType, WidgetSizeType } from '../../types';
import { WidgetWrapper } from '../WidgetWrapper/WidgetWrapper';

import { JackpotSmallWidget } from './JackpotSmallWidget/JackpotSmallWidget';
import { JackpotWideWidget } from './JackpotWideWidget/JackpotWideWidget';

interface JackpotWidgetProps {
  sizeType: WidgetSizeType;
  data: JackpotWidgetDataType;
}

/**
 * Crown Jackpot lobby rail widget. The rail — not the feature — picks the size, so this covers both
 * FE_DESIGN_SPEC §7.4 surfaces: `wide` is variant B (promo card) and `small` is variant C (round nav
 * icon, display + navigation only).
 *
 * Renders nothing unless the jackpot is visible to this player. The wrapper tap opens game-ui's
 * jackpot popup for both sizes; the promo card's own CTA opts the player in where it reads `OPT IN`
 * (see JackpotWideWidget) and opens the popup otherwise.
 */
function JackpotWidgetComponent({ data, sizeType }: JackpotWidgetProps) {
  const { gameUiStore, jackpotStore } = useStores();

  const { canShowWidget, canShowMaintenanceWidget } = jackpotStore;

  /*
   * The impression. In an effect (so it survives re-renders) and ABOVE the early returns below, because
   * a hook after a conditional return changes hook order between renders. Deduped by the store, so the
   * three lobby surfaces report one exposure between them.
   */
  useEffect(() => {
    if (canShowWidget) {
      jackpotStore.reportWidgetShown();
    }
  }, [canShowWidget, jackpotStore]);

  /*
   * Maintenance wins over size and over the tap. The design ships ONE maintenance surface — the small
   * widget's `Disabled` variant (`10929:24559`) — so the rail's choice of `wide` is overridden rather
   * than given a second maintenance layout. And there is nothing to open: game-ui reads the same flag,
   * so a popup launched here would immediately post `closeGame` and blink shut.
   */
  if (canShowMaintenanceWidget) {
    return (
      <WidgetWrapper smallWidgetExtended dataTestId="jackpot-widget-maintenance" sizeType="small">
        <JackpotSmallWidget maintenance />
      </WidgetWrapper>
    );
  }

  if (!canShowWidget) {
    return null;
  }

  return (
    <WidgetWrapper
      smallWidgetExtended
      dataTestId={data.dataTestId}
      sizeType={sizeType}
      // Reports the `widget` event; `openJackpotDialog` owns that for every entry point.
      onClick={() => gameUiStore.openJackpotDialog()}
    >
      {sizeType === 'small' ? <JackpotSmallWidget /> : <JackpotWideWidget />}
    </WidgetWrapper>
  );
}

export const JackpotWidget = observer(JackpotWidgetComponent);
